import {Model, BaseModel} from "../data";
import {Mixin} from "../common/mixwith";


/**
 * This mixin combines a dynamic template, e.g. Handlebars, with a model instance
 * and keep both synchronized. That means when the model will be updated then the
 * template will be automatically re-rendered with the new property values.
 */
export const ModelViewMixin = Mixin((superclass) => class extends superclass {

  /**
   *
   * @param config
   */
  constructor(config = {}) {
    super($.extend(true, {
      on: {
        'before:init': '_initView',
        'init': '_afterComponentInit'
      },
      modelView: {
        tpl: () => {},
        wrapper: null,
        showOnInit: true,
        data: null,
        class: Model,
        options: {},
      },
    }, config));
  }

  /**
   * Initialize a new model instance from the class defined in config
   * or take the model in data config if exists.
   * @private
   */
  _initView() {
    this._rendered = false;

    /**
     * Model instance
     * @type {BaseModel}
     */
    this.model = this.config.data;
    if (!this.model) {

      // support array for model class with multiple arguments
      if (Array.isArray(this.config.modelView.options)) {
        this.model = new this.config.modelView.class(...this.config.modelView.options);
      }
      else {
        this.model = new this.config.modelView.class(this.config.modelView.options);
      }
    }
    if ((!this.model instanceof BaseModel)) {
      throw "Invalid config in ModelBinding: data must be a Model class."
    }
    this.model.on('change', this.showView.bind(this));
    this.model.on('sync:loading', this.modelViewLoading.bind(this));
    this.model.on('sync:done', (...args) => {
      this.modelViewLoaded(...args);
      this.showView(...args);
    });
    this.model.on('sync:error', (...args) => {
      this.modelViewLoaded(...args);
    });
  }

  /**
   * Called after base component has been initialized.
   * Call {@link showView} if showOnInit is set to true.
   * @private
   */
  _afterComponentInit() {
    /**
     * DOM element the view will be appended to
     * @type {jQuery}
     */
    this.modelViewWrapper = this.resolveSelector(this.config.modelView.wrapper) || this.el;

    if (this.config.modelView.showOnInit) {
      this.showView();
    }
  }

  /**
   * Will be called before the ajax request starts loading.
   */
  modelViewLoading() {
    this.modelViewWrapper.addClass('loading');
  }

  /**
   * To be called when the requests finishes (on success and error).
   */
  modelViewLoaded() {
    this.modelViewWrapper.removeClass('loading');
  }

  /**
   * Handle the whole life cycle from a view:
   *   - renders a template with given model data
   *   - attach the view to the DOM
   *   - delegate ui, events, actions
   */
  showView() {
    if (this.triggerMethodOn('beforeShowView') === false) {
      return;
    }
    let $html = this.renderView();
    if ($html) {
      this.detachView();
      this.attachView($html);
      this._rebind();
      this._rendered = true;
      this.triggerMethodOn('showView');
    }
  }

  /**
   * Passing the model data to the 'tpl' function.
   * @return {jQuery}
   */
  renderView() {
    let data = this.model.getData();
    if (this.triggerMethodOn('beforeRenderView', [this.model, data]) === false) {
      return false;
    }
    let helpers = {
      __: (key) => this.t(key),
      format: (...args) => this.formatProperty(...args)
    };

    let html = this.config.modelView.tpl(data, {
      helpers
    });
    this.triggerMethodOn('renderView', [html]);
    return $.parseHTML(html);
  }

  /**
   * Stub reference. To be used as 'format' helper in the template.
   */
  formatProperty(...args) {}

  /**
   * Append the given $html jQuery object to the wrapping element.
   * @param {jQuery} $html - jQuery DOM object
   */
  attachView($html) {
    this.modelViewWrapper.append($html);
  }

  /**
   * Remove content from the wrapping element.
   */
  detachView() {
    this.modelViewWrapper.empty();
  }

});